import { useCallback, useEffect, useState } from 'react';
import { fetchServerVersion } from './serverVersion';
import { SW_UPDATE_EVENT, reloadToNewVersion } from './swUpdate';

/**
 * PWA 更新提示（R-06 ②、契约 §25、`05-ui-design-system.md` §5.6）。
 *
 * 比对的是「**前端产物版本** vs **服务端版本**」：同一次发布里两者由同一个 git sha 生成
 * （`packages/config/build-version.mjs` 是**唯一**计算处），所以「不相等」就是一个可判定的
 * 「你手里这份产物不是当前部署的那一版」。三个触发点：
 *   ① 启动时（本 hook 的第二个 effect）；
 *   ② SW 进入 `waiting`（浏览器发现新 SW ⇒ `main.tsx` 的注册回调广播事件 ⇒ 本 hook 的第一个 effect）；
 *   ③ 用户点「刷新」之后（`refresh` ⇒ `reloadToNewVersion`）。
 *
 * ⚠️ **判据不能只落在单测上**（§5.6 逐字登记）：Workbox / SW 的生命周期在 jsdom 里不存在。
 * 本文件钉住的是「比对逻辑与文案的接线」；「改一个商品名重新构建 ⇒ 提示出现 ⇒ 一次刷新内看到新名」
 * 这条**浏览器可观察**判据由 Task 15 的验收脚本承担。
 */

declare const __BUILD_VERSION__: string;

/**
 * 构建期注入的产物版本（`packages/config/vite.base.ts` 的 `define`）。
 *
 * `typeof` 守卫是给**非构建环境**留的（单测的 jsdom 里没有 `define`）：
 * 未注入时回空串而不是抛 `ReferenceError` —— 一个「版本号读不出来」不应该让整个收银端白屏。
 * 空串与任何真实服务端版本都不相等，而那正是我们想要的结论：读不出产物版本时**报更新**
 * 比谎报「已是最新」安全（§25 的自检口径）。
 */
export function bundleVersion(): string {
  return typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : '';
}

export interface SwUpdateState {
  /** 是否需要提示「有新版本」 */
  updateAvailable: boolean;
  /** 点「刷新」：`SKIP_WAITING` + 重载（§5.6 冻结的三步） */
  refresh: () => void;
}

export function useSwUpdate(fetchVersion: () => Promise<string> = fetchServerVersion): SwUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // ① SW 进入 waiting：注册点在 main.tsx，这里只订阅它广播出来的那一个事件
  useEffect(() => {
    const mark = (): void => setUpdateAvailable(true);
    window.addEventListener(SW_UPDATE_EVENT, mark);
    return () => {
      window.removeEventListener(SW_UPDATE_EVENT, mark);
    };
  }, []);

  // ② 启动比对：产物版本 vs 服务端版本
  useEffect(() => {
    let cancelled = false;
    void fetchVersion()
      .then((serverVersion) => {
        if (cancelled) return;
        if (serverVersion !== bundleVersion()) setUpdateAvailable(true);
      })
      .catch(() => {
        // 探活失败**不是**更新信号：静默，不打扰店员（断网时弹一条「有新版本」纯属噪声）
      });
    return () => {
      cancelled = true;
    };
  }, [fetchVersion]);

  const refresh = useCallback((): void => {
    reloadToNewVersion();
  }, []);

  return { updateAvailable, refresh };
}
